// File: frontend/src/pages/UserManagement.tsx
// Fixed to use refactored API and hooks with React Query

import React, { useState, useCallback, useMemo } from 'react';
import { Users, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { 
  useUsersWithStats, 
  useToggleUserStatus, 
  useDeleteUser,
  useUpdateUserRole,
  useResetUserPassword
} from '@/hooks/useUsers';
import { UserFilters as UserFiltersType } from '@/lib/api/userService';
import { UserTable } from '@/components/User';
import { PageHeader, PageHeaderActions, LoadingState, ErrorState, PageFilters, useFilters, StatsGrid, FilterPanel, SmartPagination } from '@/components/common/ui';
import {  UserCheck, UserX, Shield, Building, User } from 'lucide-react';
import type { FilterField } from '@/components/common/ui/PageFilters';

const UserManagement: React.FC = () => {
  // State management
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Filter state using useFilters hook
  const {
    filters: filterValues,
    updateFilter,
    clearFilters: clearFilterValues,
    hasActiveFilters
  } = useFilters({
    search: '',
    role: '',
    status: ''
  });

  // Memoize filters to prevent unnecessary re-renders
  const filters = useMemo<UserFiltersType>(() => ({
    page,
    limit,
    ...(filterValues.search && { search: filterValues.search }),
    ...(filterValues.role && { role: filterValues.role }),
    ...(filterValues.status && { status: filterValues.status === 'active' ? 'active' : filterValues.status === 'inactive' ? 'inactive' : undefined })
  }), [page, limit, filterValues.search, filterValues.role, filterValues.status]);

  // React Query hooks
  const { 
    data: usersData, 
    isLoading, 
    error, 
    refetch 
  } = useUsersWithStats(filters);

  // Mutation hooks
  const toggleStatusMutation = useToggleUserStatus();
  const deleteUserMutation = useDeleteUser();
  const updateRoleMutation = useUpdateUserRole();
  const resetPasswordMutation = useResetUserPassword();

  // Filter fields configuration
  const filterFields = useMemo<FilterField[]>(() => [
    {
      key: 'search',
      label: 'Search Users',
      type: 'search',
      placeholder: 'Search by name, email, or username...',
      width: 'lg',
      clearable: true
    },
    {
      key: 'role',
      label: 'Role',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Admin', value: 'admin' },
        { label: 'Super Admin', value: 'super_admin' },
        { label: 'Provider', value: 'provider' },
        { label: 'Customer', value: 'customer' },
        { label: 'User', value: 'user' }
      ]
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      width: 'md',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ]
    }
  ], []);

  // Event handlers
  const handleFilterChange = useCallback((key: string, value: any) => {
    updateFilter(key, value);
    setPage(1); // Reset to first page when filtering
  }, [updateFilter]);

  const handleClearFilters = useCallback(() => {
    clearFilterValues();
    setPage(1);
  }, [clearFilterValues]);

  const handleToggleStatus = useCallback(async (userId: number) => {
    try {
      await toggleStatusMutation.mutateAsync(userId);
      toast.success('User status updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user status');
    }
  }, [toggleStatusMutation]);

  const handleDeleteUser = useCallback(async (userId: number) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteUserMutation.mutateAsync(userId);
      toast.success('User deleted successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete user');
    }
  }, [deleteUserMutation]);

  const handleUpdateRole = useCallback(async (userId: number, newRole: string) => {
    try {
      await updateRoleMutation.mutateAsync({ id: userId, role: newRole });
      toast.success('User role updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update user role');
    }
  }, [updateRoleMutation]);

  const handleResetPassword = useCallback(async (userId: number) => {
    if (!window.confirm('Are you sure you want to reset this user\'s password? They will receive a new temporary password.')) {
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync(userId);
      toast.success('Password reset successfully. The user will receive the new password via email.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
    }
  }, [resetPasswordMutation]);


  // Calculate derived stats if not provided by API
  const displayStats = useMemo(() => {
    // Use integrated stats from the API response first
    if (usersData?.stats) {
      return usersData.stats;
    }
    
    // Fallback: calculate from current users data
    if (usersData?.users) {
      const users = usersData.users;
      const total = users.length;
      const active = users.filter((user: any) => user.is_active).length;
      const inactive = total - active;
      const admin = users.filter((user: any) => user.role === 'admin' || user.role === 'super_admin').length;
      const provider = users.filter((user: any) => user.role === 'provider').length;
      const customer = users.filter((user: any) => user.role === 'customer' || user.role === 'user').length;

      return {
        total,
        active,
        inactive,
        admin,
        provider,
        customer
      };
    }
    
    return null;
  }, [usersData]);

  // Loading state
  if (isLoading && !usersData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="User Management"
          subtitle="Manage system users and their permissions"
          icon={Users}
        />
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <LoadingState
            variant="skeleton"
            title="Loading users..."
            message="Please wait while we fetch user data"
            size="md"
          />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="User Management"
          subtitle="Manage system users and their permissions"
          icon={Users}
        />
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <ErrorState
            type="server"
            title="Failed to load users"
            message={error.message || 'Something went wrong. Please try again.'}
            onRetry={() => refetch()}
            size="md"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Simple Page Title - Just text, no card */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage system users and their permissions
          </p>
        </div>

        {/* Stats as Info Section - No card wrapper */}
        {(displayStats || isLoading) && (
          <div className="mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Overview</h2>
            {isLoading || !displayStats ? (
              <StatsGrid
                stats={[]}
                isLoading={true}
                loadingItems={6}
                layout="compact"
                columns={{ mobile: 2, tablet: 3, desktop: 6 }}
              />
            ) : (
              <StatsGrid
                stats={[
                  { 
                    label: 'Total Users', 
                    value: displayStats.total || 0, 
                    icon: Users,
                    color: 'blue'
                  },
                  { 
                    label: 'Active', 
                    value: displayStats.active || 0, 
                    icon: UserCheck,
                    color: 'green'
                  },
                  { 
                    label: 'Inactive', 
                    value: displayStats.inactive || 0, 
                    icon: UserX,
                    color: 'red'
                  },
                  { 
                    label: 'Admins', 
                    value: displayStats.admin || 0, 
                    icon: Shield,
                    color: 'purple'
                  },
                  { 
                    label: 'Providers', 
                    value: displayStats.provider || 0, 
                    icon: Building,
                    color: 'orange'
                  },
                  { 
                    label: 'Customers', 
                    value: displayStats.customer || 0, 
                    icon: User,
                    color: 'indigo'
                  },
                ]}
                layout="compact"
                columns={{ mobile: 2, tablet: 3, desktop: 6 }}
              />
            )}
          </div>
        )}

        {/* Filters Section - Outside card */}
        <div className="mb-6">
          <FilterPanel
            fields={filterFields}
            values={filterValues}
            onChange={handleFilterChange}
            onClear={handleClearFilters}
            hasActiveFilters={hasActiveFilters}
            variant="default"
            title="User Filters"
            defaultExpanded={hasActiveFilters}
          />
        </div>

        {/* Actions - Below filters, above listing */}
        <div className="mb-6">
          <PageHeaderActions
            secondaryActions={[
              {
                label: "Refresh",
                icon: RefreshCw,
                onClick: () => refetch(),
                variant: "outline",
                loading: isLoading,
              },
            ]}
          />
        </div>

        {/* Mobile: No card wrapper for max space */}
        <div className="block xl:hidden">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Users</h3>
            {usersData && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                {usersData.pagination?.total || usersData.users?.length || 0} total
              </span>
            )}
          </div>
          <UserTable
            users={usersData?.users || []}
            isLoading={isLoading}
            searchTerm={filterValues.search}
            roleFilter={filterValues.role}
            statusFilter={filterValues.status}
            pagination={usersData?.pagination}
            currentPage={page}
            onPageChange={setPage}
            onToggleStatus={handleToggleStatus}
            onDeleteUser={handleDeleteUser}
            onUpdateRole={handleUpdateRole}
            onResetPassword={handleResetPassword}
            isTogglingStatus={toggleStatusMutation.isPending}
            isDeletingUser={deleteUserMutation.isPending}
            isUpdatingRole={updateRoleMutation.isPending}
            isResettingPassword={resetPasswordMutation.isPending}
          />
        </div>

        {/* Desktop: Card wrapper for clean look */}
        <div className="hidden xl:block bg-white rounded-lg border border-gray-200 shadow-sm">
          {/* Card Header with count */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Users</h3>
              {usersData && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  {usersData.pagination?.total || usersData.users?.length || 0} total
                </span>
              )}
            </div>
          </div>

          {/* Users Listing */}
          <div className="p-6">
            <UserTable
              users={usersData?.users || []}
              isLoading={isLoading}
              searchTerm={filterValues.search}
              roleFilter={filterValues.role}
              statusFilter={filterValues.status}
              pagination={usersData?.pagination}
              currentPage={page}
              onPageChange={setPage}
              onToggleStatus={handleToggleStatus}
              onDeleteUser={handleDeleteUser}
              onUpdateRole={handleUpdateRole}
              onResetPassword={handleResetPassword}
              isTogglingStatus={toggleStatusMutation.isPending}
              isDeletingUser={deleteUserMutation.isPending}
              isUpdatingRole={updateRoleMutation.isPending}
              isResettingPassword={resetPasswordMutation.isPending}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;